/**
 * The native shell's one mounted component: the things a WebView needs that a browser does not.
 *
 * Mounted once, in `__root.tsx`, above every route. On the web it installs nothing and renders
 * nothing — `isNativePlatform()` is false and every effect returns early — so the browser build
 * pays one component that does no work.
 *
 * It is a component rather than a hook because the back button's confirmation has to be *shown*,
 * and this client has no toast system to borrow. The prompt is a short-lived pill in the same
 * translucent treatment the HUD uses, drawn above everything and touching nothing: it must not
 * eat the press that follows it, which is the press it exists to ask for.
 */

import { App } from '@capacitor/app';
import { useNavigate } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { applyCompositorProfile, compositorProfile } from '../game/render-profile';
import { BACK_CONFIRM_WINDOW_MS, decideBack } from './back-button';
import { configureStatusBar, isNativePlatform } from './platform';

export function NativeShell() {
  const navigate = useNavigate();
  const [prompt, setPrompt] = useState<string | null>(null);

  // The listener is registered once and must never close over a stale navigate.
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  useEffect(() => {
    if (!isNativePlatform()) return;
    void configureStatusBar();
    // The HUD's backdrop blurs, dropped for the whole life of the shell. It is set here rather
    // than in `GameCanvas` because it is a property of the *surface* and not of a match: the
    // blurred panels outside the arena cost the same to composite, and a profile that arrived
    // when a game started would be a visible restyle every time somebody pressed Play.
    applyCompositorProfile(document.documentElement, compositorProfile({ native: true }));

    let armed = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    /** Drop a pending confirmation: the flag, its timer and the pill together. */
    const disarm = () => {
      armed = false;
      if (timer !== null) clearTimeout(timer);
      timer = null;
      setPrompt(null);
    };

    const handleBack = () => {
      // Read at press time, not captured: one listener, and it always sees the live route.
      const action = decideBack(window.location.pathname, armed);
      switch (action.kind) {
        case 'confirm':
          armed = true;
          setPrompt(action.prompt);
          timer = setTimeout(disarm, BACK_CONFIRM_WINDOW_MS);
          return;
        case 'exit':
          disarm();
          void App.exitApp();
          return;
        case 'leave-arena':
          disarm();
          void navigateRef.current({ to: '/' });
          return;
        default:
          window.history.back();
      }
    };

    let backHandle: { remove: () => Promise<void> } | null = null;
    let stateHandle: { remove: () => Promise<void> } | null = null;
    let cancelled = false;

    void App.addListener('backButton', handleBack).then((handle) => {
      if (cancelled) void handle.remove();
      else backHandle = handle;
    });

    // An armed prompt survives being backgrounded — the timer that would have cleared it is
    // frozen with everything else — so a resume would otherwise come back to a stale question
    // whose second press means something different by then.
    void App.addListener('appStateChange', ({ isActive }) => {
      if (isActive) disarm();
    }).then((handle) => {
      if (cancelled) void handle.remove();
      else stateHandle = handle;
    });

    return () => {
      cancelled = true;
      void backHandle?.remove();
      void stateHandle?.remove();
      disarm();
    };
  }, []);

  if (prompt === null) return null;

  return (
    <div
      role="status"
      data-testid="back-confirm"
      className="pointer-events-none fixed inset-x-0 bottom-0 z-50 mx-auto mb-[calc(1.5rem+var(--chomp-safe-bottom))] w-fit max-w-[80%] rounded-full border border-border/50 bg-card/80 px-4 py-2 text-center text-sm text-foreground shadow-lg backdrop-blur-md"
    >
      {prompt}
    </div>
  );
}
